import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'src/components/ui/toaster';
import { useAuth } from 'src/hooks/useAuth';

import MainLayout from 'src/layouts/MainLayout/MainLayout';
import LoginLayout from 'src/layouts/MainLayout/LoginLayout';
import Login from 'src/pages/Auth/Login/Login';
import Signup from 'src/pages/Auth/Signup/Signup';
import ChangePassword from 'src/pages/Auth/ChangePassword/ChangePassword';
import ResetPassword from 'src/pages/Auth/ResetPassword/ResetPassword';
import CreatePersona from 'src/pages/Persona/CreatePersona/CreatePersona';
import UploadResume from 'src/pages/Resume/UploadResume/UploadResume';
import NotFound from 'src/pages/NotFound/NotFound';
import Dashboard from 'src/pages/Dashboard/Dashboard';
import ListPersonas from 'src/pages/Persona/ListPersonas/ListPersonas';
import Opportunities from 'src/pages/Customer/Opportunities/Opportunities';
import OpportunityDetails from 'src/pages/Customer/OpportunityDetails/OpportunityDetails';
import Search from 'src/pages/Candidate/Search/Search';
import Preview from 'src/pages/Candidate/Preview/Preview';
import TestManagementDashboard from 'src/pages/TestManagement/TestManagement';
import TestCollectionForm from 'src/pages/TestManagement/TestCollectionForm/TestCollectionForm';
import TestGroupForm from 'src/pages/TestManagement/TestGroupForm/TestGroupForm';
import TestCollectionPreview from 'src/pages/TestManagement/TestCollectionPreview/TestCollectionPreview';
import TestGroupPreview from 'src/pages/TestManagement/TestGroupPreview/TestGroupPreview';
import SubmitCandidate from 'src/pages/Candidate/SubmitCandidate/SubmitCandidate';
import RequirementPipeline from 'src/pages/Candidate/RequirementPipeline/RequirementPipeline';
import AssignmentPipeline from 'src/pages/Candidate/AssignmentPipeline/AssignmentPipeline';
import UpdateScoreForm from 'src/pages/Candidate/UpdateScoreForm/UpdateScoreForm';
import ComingSoon from 'src/pages/ComingSoon/ComingSoon';
import ApplicationSettings from 'src/pages/ApplicationSettings/ApplicationSettings';
import Organizations from 'src/pages/Organization/Organizations';
import AhedgeUsers from 'src/pages/UserManagement/AhedgeUsers';
import OrganizationUsers from 'src/pages/UserManagement/OrganizationUsers';
import OrganizationUsersDetail from 'src/pages/UserManagement/OrganizationUsersDetail';
import ListBilling from 'src/pages/Billing/ListBilling/ListBilling';
import BillingInfo from 'src/pages/Billing/BillingInfo/BillingInfo';
import CandidateInfo from 'src/pages/Candidate/CandidateInfo/CandidateInfo';
import SourceCandidate from 'src/pages/Candidate/SourceCandidate/SourceCandidate';
import CreateRequirement from 'src/pages/Candidate/SourceCandidate/CreateRequirement/CreateRequirement';
import RequirementSubmissions from 'src/pages/Candidate/SourceCandidate/RequirementSubmissions/RequirementSubmissions';
import RequirementReadOnlyView from 'src/pages/Candidate/SourceCandidate/RequirementReadOnlyView/RequirementReadOnlyView';
import AssignPartners from 'src/pages/Candidate/SourceCandidate/AssignPartners/AssignPartners';
import ListMilestones from 'src/pages/Billing/ListMilestones/ListMilestones';
import ListInvoices from 'src/pages/Billing/ListInvoices/ListInvoices';
import InvoiceInfo from 'src/pages/Billing/InvoiceInfo/InvoiceInfo';
import ListProformaInvoices from 'src/pages/Billing/ListProformaInvoices/ListProformaInvoices';
import ProformaInvoiceInfo from 'src/pages/Billing/ProformaInvoiceInfo/ProformaInvoiceInfo';
import ListCreditNotes from 'src/pages/Billing/ListCreditNotes/ListCreditNotes';
import CreditNotesInfo from 'src/pages/Billing/CreditNotesInfo/CreditNotesInfo';
import Partners from 'src/pages/Partner/Partners';
import PartnerDetail from 'src/pages/Partner/PartnerDetail';
import PartnerUsers from 'src/pages/Partner/PartnerUsers';
import JobPostings from 'src/pages/JobPostings/JobPostings';
import CreateJobPosting from 'src/pages/JobPostings/CreateJobPosting';
import JobPostingDetail from 'src/pages/JobPostings/JobPostingDetail';
import Vendors from 'src/pages/Vendor/Vendors';
import AssessmentTypes from 'src/pages/AssessmentTypes/AssessmentTypes';
import UploadTracker from 'src/pages/UploadTracker/UploadTracker';
import UploadTrackerJobDetail from 'src/pages/UploadTracker/JobDetail';
import SkillResolution from 'src/pages/SkillResolution/SkillResolution';
import UpcomingInterviews from 'src/pages/Interviewer/UpcomingInterviews/UpcomingInterviews';
import InterviewBrief from 'src/pages/Interviewer/InterviewBrief/InterviewBrief';

const ProtectedRoute = ({ children }) => {
  const { getUserInfo } = useAuth();
  const user = getUserInfo();

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  return children;
};

const GuestRoute = ({ children }) => {
  const { getUserInfo, isOnlyRole } = useAuth();
  const user = getUserInfo();

  if (user) {
    return (
      <Navigate to={isOnlyRole('ahedge_admin') ? '/personas' : '/'} replace />
    );
  }

  return children;
};

const LandingPage = () => {
  const { isOnlyRole } = useAuth();

  // If this is the user's only real role, redirect to ListPersonas.
  if (isOnlyRole('ahedge_admin')) {
    return <Navigate to="/personas" replace />;
  }

  // Same idea for a pure interviewer — Opportunities has nothing for them
  // (its own isCse/isTa checks are both false), so land them on their own
  // page instead of an empty CSE view.
  if (isOnlyRole('ahedge_interviewer')) {
    return <Navigate to="/interviews/upcoming" replace />;
  }

  // Otherwise, show Opportunities
  return <Opportunities />;
};

const AppRouter = () => {
  // Common routes for both CSE and TA
  const sharedRoutesForNonAdmin = [
    { path: '/dashboard', element: <Dashboard /> },
    { path: '/opportunities', element: <Opportunities /> },
    { path: '/opportunity/:id', element: <OpportunityDetails /> },
    { path: '/opportunity/:id/candidates', element: <RequirementPipeline /> },
    { path: '/candidates/submit', element: <SubmitCandidate /> },
    { path: '/candidates/list', element: <RequirementPipeline /> },
    { path: '/candidates/update-score', element: <UpdateScoreForm /> },
    { path: '/candidates/:id', element: <Preview /> },
    { path: '/candidate/:id', element: <CandidateInfo /> },
  ];

  const sharedRoutesForAll = [
    { path: '/change-password', element: <ChangePassword /> },
    { path: '/404', element: <NotFound /> },
    { path: '/coming-soon', element: <ComingSoon /> },
  ];

  // CSE-specific routes
  const cseRoutes = [
    { path: '/candidates/search', element: <Search /> },
    { path: '/purchase-order', element: <ListBilling /> },
    { path: '/purchase-order/:id', element: <BillingInfo /> },
    { path: '/milestones', element: <ListMilestones /> },
    { path: '/invoices', element: <ListInvoices /> },
    { path: '/credit-notes', element: <ListCreditNotes /> },
    { path: '/invoices/:id', element: <InvoiceInfo /> },
    { path: '/credit-notes/:id', element: <CreditNotesInfo /> },
    { path: '/proforma-invoices', element: <ListProformaInvoices /> },
    { path: '/proforma-invoices/:id', element: <ProformaInvoiceInfo /> },
    { path: '/vendors', element: <Vendors /> },
    { path: '/skill-resolution', element: <SkillResolution /> },
  ];

  // TA-specific routes
  const taRoutes = [
    { path: '/resume/upload', element: <UploadResume /> },
    { path: '/source-candidates', element: <SourceCandidate /> },
    { path: '/job-postings', element: <JobPostings /> },
    { path: '/job-postings/create', element: <CreateJobPosting /> },
    { path: '/job-postings/:id', element: <JobPostingDetail /> },
    {
      path: '/source-candidates/create',
      element: <CreateRequirement />,
    },
    {
      path: '/source-candidates/preview/:id',
      element: <RequirementReadOnlyView />,
    },
    {
      path: '/source-candidates/:id/assign-partners',
      element: <AssignPartners />,
    },
    {
      path: '/source-candidates/:id',
      element: <RequirementSubmissions />,
    },
    { path: '/assignment-pipeline', element: <AssignmentPipeline /> },
  ];

  // Interviewer-specific routes
  const interviewerRoutes = [
    { path: '/interviews/upcoming', element: <UpcomingInterviews /> },
    {
      path: '/interviews/:interviewRoundDetailId/brief',
      element: <InterviewBrief />,
    },
  ];

  // Admin specific routes
  const adminRoutes = [
    { path: '/persona/create', element: <CreatePersona /> },
    { path: '/persona/edit/:id', element: <CreatePersona /> },
    { path: '/personas', element: <ListPersonas /> },
    { path: '/tests', element: <TestManagementDashboard /> },
    { path: '/tests/collection/new', element: <TestCollectionForm /> },
    { path: '/tests/collection/edit/:id', element: <TestCollectionForm /> },
    { path: '/tests/group/new', element: <TestGroupForm /> },
    { path: '/tests/group/edit/:id', element: <TestGroupForm /> },
    {
      path: '/tests/collection/preview/:id',
      element: <TestCollectionPreview />,
    },
    { path: '/tests/group/preview/:id', element: <TestGroupPreview /> },
    { path: '/application-settings', element: <ApplicationSettings /> },
    { path: '/assessment-types', element: <AssessmentTypes /> },
    { path: '/organizations', element: <Organizations /> },
    { path: '/partners', element: <Partners /> },
    { path: '/partners/:orgId', element: <PartnerDetail /> },
    { path: '/ahedge-users', element: <AhedgeUsers /> },
    { path: '/organization-users', element: <OrganizationUsers /> },
    {
      path: '/partner-users',
      element: <Partners isUserManagement />,
    },
    {
      path: '/partner-users/:orgId',
      element: <PartnerUsers />,
    },
    {
      path: '/organization-users/:organizationId',
      element: <OrganizationUsersDetail />,
    },
  ];

  return (
    <>
      <Toaster />
      <BrowserRouter>
        <Routes>
          {/* Public routes — redirect authenticated users away */}
          <Route
            element={
              <GuestRoute>
                <LoginLayout />
              </GuestRoute>
            }
          >
            <Route path="/login" element={<Login />} />
            <Route path="/signup" element={<Signup />} />
            <Route
              path="/internal/reset-password"
              element={<ResetPassword />}
            />
          </Route>

          {/* Protected routes */}
          <Route
            path="/"
            element={
              <ProtectedRoute
                allowedRoles={['ahedge_cse', 'ahedge_ta', 'ahedge_admin']}
              >
                <MainLayout />
              </ProtectedRoute>
            }
          >
            {/* Root path - redirects to ListPersonas if user is only admin, otherwise shows Opportunities */}
            <Route index element={<LandingPage />} />

            {/* Shared routes */}
            {sharedRoutesForNonAdmin.map((route) => (
              <Route
                key={route.path}
                path={route.path}
                element={
                  <ProtectedRoute allowedRoles={['ahedge_cse', 'ahedge_ta']}>
                    {route.element}
                  </ProtectedRoute>
                }
              />
            ))}

            {/* Shared routes for all */}
            {sharedRoutesForAll.map((route) => (
              <Route
                key={route.path}
                path={route.path}
                element={<ProtectedRoute>{route.element}</ProtectedRoute>}
                allowedRoles={['ahedge_cse', 'ahedge_ta', 'ahedge_admin']}
              />
            ))}

            {/* CSE-specific routes */}
            {cseRoutes.map((route) => (
              <Route
                key={route.path}
                path={route.path}
                element={
                  <ProtectedRoute allowedRoles={['ahedge_cse']}>
                    {route.element}
                  </ProtectedRoute>
                }
              />
            ))}

            {/* TA-specific routes */}
            {taRoutes.map((route) => (
              <Route
                key={route.path}
                path={route.path}
                element={
                  <ProtectedRoute allowedRoles={['ahedge_ta']}>
                    {route.element}
                  </ProtectedRoute>
                }
              />
            ))}
            {/* Interviewer-specific routes */}
            {interviewerRoutes.map((route) => (
              <Route
                key={route.path}
                path={route.path}
                element={
                  <ProtectedRoute allowedRoles={['ahedge_interviewer']}>
                    {route.element}
                  </ProtectedRoute>
                }
              />
            ))}

            {/* Admin-specific routes */}
            {adminRoutes.map((route) => (
              <Route
                key={route.path}
                path={route.path}
                element={
                  <ProtectedRoute allowedRoles={['ahedge_admin']}>
                    {route.element}
                  </ProtectedRoute>
                }
              />
            ))}

            {/* Upload Tracker — shared by CSE, TA and Admin */}
            <Route
              path="/upload-tracker"
              element={
                <ProtectedRoute
                  allowedRoles={['ahedge_cse', 'ahedge_ta', 'ahedge_admin']}
                >
                  <UploadTracker />
                </ProtectedRoute>
              }
            />
            <Route
              path="/upload-tracker/:jobRef"
              element={
                <ProtectedRoute
                  allowedRoles={['ahedge_cse', 'ahedge_ta', 'ahedge_admin']}
                >
                  <UploadTrackerJobDetail />
                </ProtectedRoute>
              }
            />

            {/* Catch-all route */}
            <Route path="*" element={<Navigate to="/404" replace />} />
          </Route>
        </Routes>
      </BrowserRouter>
    </>
  );
};

export default AppRouter;
